/**
 * Generate WAV file data from audio samples
 * @param samples - Float32Array of audio samples
 * @param sampleRate - Sample rate in Hz
 * @returns ArrayBuffer containing WAV file data
 */
export function generateWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  // Validate input
  if (samples.length === 0) {
    throw new Error('Cannot generate WAV file from empty sample array');
  }
  
  // Prevent excessively large files (> 10 minutes at 44.1kHz)
  const maxSamples = 44100 * 60 * 10;
  if (samples.length > maxSamples) {
    throw new Error(`Sample array too large: ${samples.length} samples (max: ${maxSamples})`);
  }
  
  const numChannels = 1; // Mono
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  
  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  
  // fmt sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // Sub-chunk size
  view.setUint16(20, 1, true); // Audio format (1 = PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  
  // data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);
  
  // Write audio data
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    // Convert float to 16-bit PCM
    const sample = Math.max(-1, Math.min(1, samples[i]));
    const pcmSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    view.setInt16(offset, pcmSample, true);
    offset += 2;
  }
  
  return buffer;
}

/**
 * Helper function to write string to DataView
 */
function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

/**
 * Create a Blob URL from WAV data
 */
export function createWavBlobUrl(wavData: ArrayBuffer): string {
  const blob = new Blob([wavData], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
}
